import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Button, Spinner, Alert, Card, Badge, Toast, ToastContainer } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { formatDistanceToNow } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { 
  FaShareAlt, 
  FaCopy, 
  FaEye, 
  FaDownload,
  FaArrowLeft,
  FaCalendarAlt,
  FaUser,
  FaTag,
  FaImage,
  FaFileAlt,
  FaStar,
  FaImages,
  FaFile,
  FaVideo,
  FaMusic,
  FaNewspaper,
  FaChartBar,
  FaBullhorn,
  FaPenAlt,
  FaPaperclip,
  FaShareSquare,
  FaExternalLinkAlt,
  FaPlay,
  FaHeadphones,
  FaBookOpen,
  FaExclamationCircle,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaCompress,
  FaTimes
} from 'react-icons/fa';

const ContentDetail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [contentId, setContentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedContent, setRelatedContent] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const [tokenData, setTokenData] = useState(null);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  
  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [sliderItems, setSliderItems] = useState([]);
  const [sliderError, setSliderError] = useState({});
  
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const sliderRef = useRef(null);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prepare slider items when content is loaded
  useEffect(() => {
    if (content) {
      const items = [];
      
      // Add featured media
      if (content.featured_media || content.featured_media_data?.url) {
        const featuredUrl = content.featured_media || content.featured_media_data?.url;
        const featuredData = content.featured_media_data || {};
        
        items.push({
          type: content.content_type === 'video' ? 'video' : 
                content.content_type === 'audio' ? 'audio' : 
                content.content_type === 'image' ? 'image' : 'unknown',
          url: featuredUrl,
          title: 'Featured',
          data: featuredData,
          isFeatured: true
        });
      }
      
      // Add additional media
      if (content.additional_media && Array.isArray(content.additional_media)) {
        content.additional_media.forEach((media, index) => {
          const mediaUrl = typeof media === 'string' ? media : (media?.url || media);
          const mediaData = typeof media === 'object' ? media : null;
          const mediaType = mediaData?.resource_type || 
                           (mediaUrl?.includes('video') ? 'video' : 
                            mediaUrl?.includes('audio') ? 'audio' : 'image');
          
          items.push({
            type: mediaType,
            url: mediaUrl,
            title: mediaData?.original_filename || `Media ${index + 1}`,
            data: mediaData,
            isFeatured: false
          });
        });
      }
      
      // If no media items, add a placeholder
      if (items.length === 0) {
        items.push({
          type: 'placeholder',
          url: null,
          title: 'No media available',
          isFeatured: false
        });
      }
      
      setSliderItems(items);
      setCurrentSlide(0);
    }
  }, [content]);

  // Show toast message
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => {
      setToast({ ...toast, show: false });
    }, 3000);
  };

  // Show access restricted message
  const showAccessRestricted = () => {
    showToast('Media access is restricted', 'warning');
  };

  const getContentTypeIcon = (type) => {
    const icons = {
      'video': <FaVideo size={16} />,
      'image': <FaImage size={16} />,
      'audio': <FaMusic size={16} />,
      'document': <FaFileAlt size={16} />,
      'news': <FaNewspaper size={16} />,
      'report': <FaChartBar size={16} />,
      'statement': <FaBullhorn size={16} />,
      'article': <FaPenAlt size={16} />,
      'default': <FaPaperclip size={16} />
    };
    return icons[type] || icons.default;
  };

  const getContentTypeText = (type) => {
    const types = {
      'video': 'Video',
      'image': 'Image',
      'audio': 'Audio',
      'document': 'Document',
      'news': 'News',
      'report': 'Report',
      'statement': 'Statement',
      'article': 'Article',
      'default': 'Content'
    };
    return types[type] || types.default;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: enUS
      });
    } catch (error) {
      return new Date(dateString).toLocaleDateString('en-US');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
  };

  // Generate Cloudinary thumbnail URL for video
  const generateCloudinaryThumbnail = (videoUrl, options = {}) => {
    if (!videoUrl) return null;
    
    const url = new URL(videoUrl);
    const pathParts = url.pathname.split('/');
    const uploadIndex = pathParts.indexOf('upload');
    
    if (uploadIndex === -1) return videoUrl;
    
    const defaultOptions = {
      width: 400,
      height: 225,
      crop: 'fill',
      quality: 'auto:good',
      format: 'jpg'
    };
    
    const thumbOptions = { ...defaultOptions, ...options };
    
    const transformations = [
      `c_${thumbOptions.crop}`,
      `w_${thumbOptions.width}`,
      `h_${thumbOptions.height}`,
      `q_${thumbOptions.quality}`,
      `f_${thumbOptions.format}`
    ].join(',');
    
    return videoUrl.replace('/upload/', `/upload/${transformations}/`);
  };

  // Get thumbnail URL for different content types
  const getThumbnailUrl = (item) => {
    if (!item.url) return null;
    
    if (item.type === 'video') {
      if (item.url.includes('cloudinary.com')) {
        return generateCloudinaryThumbnail(item.url);
      }
      return item.url;
    }
    
    if (item.type === 'image') {
      return item.url;
    }
    
    return null;
  };

  // Get fallback icon for content type
  const getFallbackIcon = (type) => {
    switch (type) {
      case 'video':
        return '🎬';
      case 'image':
        return '🖼️';
      case 'audio':
        return '🎵';
      case 'document':
        return '📄';
      default:
        return '📎';
    }
  };

  const getVideoMimeType = (url) => {
    if (url.includes('.mp4')) return 'video/mp4';
    if (url.includes('.webm')) return 'video/webm';
    if (url.includes('.ogg') || url.includes('.ogv')) return 'video/ogg';
    if (url.includes('.mov')) return 'video/quicktime';
    if (url.includes('.avi')) return 'video/x-msvideo';
    return 'video/mp4';
  };

  const getAudioMimeType = (url) => {
    if (url.includes('.mp3')) return 'audio/mpeg';
    if (url.includes('.wav')) return 'audio/wav';
    if (url.includes('.ogg')) return 'audio/ogg';
    if (url.includes('.m4a')) return 'audio/mp4';
    if (url.includes('.flac')) return 'audio/flac';
    return 'audio/mpeg';
  };

  // Slider navigation
  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev === 0 ? sliderItems.length - 1 : prev - 1));
    // Pause any playing video when navigating
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev === sliderItems.length - 1 ? 0 : prev + 1));
    // Pause any playing video when navigating
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const toggleFullscreen = () => {
    setShowFullscreen(!showFullscreen);
  };

  const handleImageError = (index) => {
    setSliderError(prev => ({ ...prev, [index]: true }));
  };

  // Render slider item based on type
  const renderSliderItem = (item, index, isFullscreen = false) => {
    const isActive = index === currentSlide;
    const thumbnailUrl = getThumbnailUrl(item);
    const hasError = sliderError[index];

    if (item.type === 'video') {
      return (
        <div className={`slider-item ${isActive ? 'active' : ''}`} style={{ height: '100%' }}>
          <video
            ref={isActive ? videoRef : null}
            controls
            autoPlay={isActive && isPlaying}
            muted={isActive && isPlaying}
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              backgroundColor: '#000'
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          >
            <source src={item.url} type={getVideoMimeType(item.url)} />
            Your browser does not support video playback.
          </video>
        </div>
      );
    }

    if (item.type === 'audio') {
      return (
        <div className={`slider-item ${isActive ? 'active' : ''}`} style={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa',
          padding: '20px'
        }}>
          <div className="audio-placeholder mb-4" style={{ fontSize: '4rem' }}>
            <FaHeadphones size={80} color="#255853" />
          </div>
          <h4 className="mb-3">{item.title}</h4>
          <audio
            ref={isActive ? audioRef : null}
            controls
            style={{ width: '100%', maxWidth: '500px' }}
          >
            <source src={item.url} type={getAudioMimeType(item.url)} />
            Your browser does not support audio playback.
          </audio>
        </div>
      );
    }

    if (item.type === 'image') {
      return (
        <div className={`slider-item ${isActive ? 'active' : ''}`} style={{ 
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          {!hasError ? (
            <img
              src={item.url}
              alt={item.title}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain'
              }}
              onError={() => handleImageError(index)}
            />
          ) : (
            <div className="image-error" style={{ textAlign: 'center' }}>
              <FaImage size={64} color="#6c757d" />
              <p className="mt-3">Failed to load image</p>
            </div>
          )}
        </div>
      );
    }

    // Placeholder or unknown type
    return (
      <div className={`slider-item ${isActive ? 'active' : ''}`} style={{ 
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="placeholder-content" style={{ textAlign: 'center' }}>
          <FaFileAlt size={64} color="#6c757d" />
          <p className="mt-3">{item.title || 'No media available'}</p>
        </div>
      </div>
    );
  };

  // Fetch token data from content_tokens table
  const fetchTokenData = async () => {
    try {
      const { data, error } = await supabase
        .from('content_tokens')
        .select('*')
        .eq('token', token)
        .single();

      if (error) throw error;

      if (data) {
        setTokenData(data);
        
        // Check if token is valid
        const now = new Date();
        const expiresAt = new Date(data.expires_at);
        const isExpired = data.expires_at && expiresAt < now;
        const hasUsesLeft = !data.max_uses || (data.uses_count || 0) < data.max_uses;
        
        if (isExpired || !hasUsesLeft) {
          setIsTokenValid(false);
          setError('Invalid or expired token');
          return null;
        } else {
          setIsTokenValid(true);
          // Increment token usage
          await supabase
            .from('content_tokens')
            .update({ uses_count: (data.uses_count || 0) + 1 })
            .eq('id', data.id);
          
          return data.content_id;
        }
      } else {
        setIsTokenValid(false);
        setError('Invalid or expired token');
        return null;
      }
    } catch (error) {
      console.error('Error fetching token:', error);
      setIsTokenValid(false);
      setError('Invalid or expired token');
      return null;
    }
  };

  // Fetch content by ID
  const fetchContentById = async (id) => {
    try {
      const { data, error } = await supabase
        .from('content')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        console.log('Fetched content:', data);
        setContent(data);
        setContentId(data.id);
        
        // Fetch related content
        fetchRelatedContent(data.category, data.content_type, data.id);
        
        return data;
      } else {
        setError('Content not found');
        return null;
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      setError('Content not found');
      return null;
    }
  };

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching content for token:', token);
      
      // First, fetch token data to get content ID
      const contentIdFromToken = await fetchTokenData();
      
      if (contentIdFromToken && isTokenValid) {
        console.log('Token valid, fetching content ID:', contentIdFromToken);
        
        // Then fetch the actual content
        await fetchContentById(contentIdFromToken);
      } else {
        setError('Invalid or expired token');
      }
    } catch (error) {
      console.error('Error in fetchContent:', error);
      setError('Content not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedContent = async (category, contentType, excludeId) => {
    try {
      const { data } = await supabase
        .from('content')
        .select('*')
        .eq('status', 'published')
        .neq('id', excludeId)
        .order('created_at', { ascending: false })
        .limit(4);

      if (data) {
        // Fetch tokens for related content
        const relatedWithTokens = await fetchTokensForRelatedContent(data);
        setRelatedContent(relatedWithTokens);
      }
    } catch (error) {
      console.error('Error fetching related content:', error);
    }
  };

  // Fetch tokens for related content
  const fetchTokensForRelatedContent = async (contentItems) => {
    try {
      const contentIds = contentItems.map(item => item.id);
      
      const { data: tokensData, error } = await supabase
        .from('content_tokens')
        .select('token, content_id')
        .in('content_id', contentIds);

      if (error) throw error;

      const tokenMap = {};
      if (tokensData) {
        tokensData.forEach(token => {
          tokenMap[token.content_id] = token.token;
        });
      }

      return contentItems.map(item => ({
        ...item,
        content_token: tokenMap[item.id] || null
      }));
    } catch (error) {
      console.error('Error fetching tokens for related content:', error);
      return contentItems.map(item => ({
        ...item,
        content_token: null
      }));
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        showToast('Link copied!');
      })
      .catch(err => {
        console.error('Failed to copy:', err);
        showToast('Failed to copy link', 'danger');
      });
  };

  const shareContent = () => {
    if (!content) return;
    
    if (navigator.share) {
      navigator.share({
        title: content.title,
        text: content.description,
        url: window.location.href,
      }).catch(err => {
        console.error('Error sharing:', err);
        copyToClipboard();
      });
    } else {
      copyToClipboard();
    }
  };

  useEffect(() => {
    if (token) {
      fetchContent();
    }
  }, [token]);

  // Add CSS styles
  const styles = `
    .content-detail-container {
      min-height: 100vh;
      background: white;
      padding: ${isMobile ? '10px' : '20px'};
      direction: ltr;
      font-family: 'ITF Qomra Arabic', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .back-button {
      position: fixed;
      top: ${isMobile ? '10px' : '20px'};
      left: ${isMobile ? '10px' : '20px'};
      z-index: 1000;
    }

    .content-header {
      background: #fff;
      border-radius: 12px;
      padding: ${isMobile ? '15px' : '25px'};
      margin-bottom: ${isMobile ? '15px' : '25px'};
      border: 1px solid #e0e6ed;
      margin-top: ${isMobile ? '50px' : '0'};
    }

    .content-title {
      font-size: ${isMobile ? '1.3rem' : '1.8rem'};
      font-weight: 700;
      color: #255853;
      margin-bottom: 15px;
      line-height: 1.4;
    }

    .meta-info {
      display: flex;
      flex-wrap: wrap;
      gap: ${isMobile ? '8px' : '12px'};
      margin-bottom: 20px;
      align-items: center;
    }

    @media (max-width: 768px) {
      .meta-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
      }
    }

    .meta-badge {
      background-color: #255853;
      color: white;
      padding: 6px 14px;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 600;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      white-space: nowrap;
    }

    .meta-text {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #6c757d;
      font-size: 0.9rem;
    }

    .action-button {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: ${isMobile ? '8px 15px' : '10px 18px'};
      border-radius: 25px;
      border: 2px solid;
      background-color: white;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 0.95rem;
      font-weight: 500;
      min-width: ${isMobile ? '100%' : '120px'};
      height: ${isMobile ? '45px' : 'auto'};
    }

    .action-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }

    .share-button {
      color: #007bff;
      background-color: white;
      border-color: #007bff;
    }

    .share-button:hover {
      background-color: #007bff;
      color: white;
    }

    /* Slider Styles */
    .slider-section {
      background: #fff;
      border-radius: 12px;
      padding: ${isMobile ? '15px' : '25px'};
      margin-bottom: ${isMobile ? '15px' : '25px'};
      border: 1px solid #e0e6ed;
    }

    .slider-title {
      font-size: ${isMobile ? '1.1rem' : '1.2rem'};
      font-weight: 600;
      color: #255853;
      margin-bottom: 15px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .slider-container {
      position: relative;
      width: 100%;
      height: ${isMobile ? '300px' : '400px'};
      background: #f8f9fa;
      border-radius: 8px;
      overflow: hidden;
      margin-bottom: 15px;
    }

    .slider-wrapper {
      position: relative;
      width: 100%;
      height: 100%;
    }

    .slider-item {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: opacity 0.3s ease;
      pointer-events: none;
    }

    .slider-item.active {
      opacity: 1;
      pointer-events: all;
    }

    .slider-controls {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 40px;
      height: 40px;
      background: rgba(37, 88, 83, 0.8);
      color: white;
      border: none;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      z-index: 10;
    }

    .slider-controls:hover {
      background: #255853;
      transform: translateY(-50%) scale(1.1);
    }

    .slider-controls.prev {
      left: 15px;
    }

    .slider-controls.next {
      right: 15px;
    }

    .fullscreen-control {
      position: absolute;
      bottom: 15px;
      right: 15px;
      width: 40px;
      height: 40px;
      background: rgba(37, 88, 83, 0.8);
      color: white;
      border: none;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      z-index: 20;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
    }

    .fullscreen-control:hover {
      background: #255853;
      transform: scale(1.1);
    }

    .slider-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin-top: 10px;
      flex-wrap: wrap;
    }

    .slider-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #dee2e6;
      border: none;
      padding: 0;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .slider-dot:hover {
      background: #255853;
    }

    .slider-dot.active {
      width: 30px;
      border-radius: 10px;
      background: #255853;
    }

    /* Fullscreen Modal */
    .fullscreen-modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.98);
      z-index: 9999;
      display: flex;
      flex-direction: column;
    }

    .fullscreen-header {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      padding: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      z-index: 10;
      background: linear-gradient(to bottom, rgba(0,0,0,0.5), transparent);
    }

    .fullscreen-title {
      color: white;
      font-size: 1.2rem;
      font-weight: 600;
      margin: 0;
    }

    .fullscreen-close {
      background: rgba(255, 255, 255, 0.2);
      border: none;
      color: white;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 1.2rem;
    }

    .fullscreen-close:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: scale(1.1);
    }

    .fullscreen-content {
      flex: 1;
      position: relative;
      width: 100%;
      height: 100%;
    }

    .fullscreen-controls {
      position: absolute;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 20px;
      z-index: 10;
    }

    .fullscreen-nav {
      width: 50px;
      height: 50px;
      background: rgba(255, 255, 255, 0.2);
      color: white;
      border: 2px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .fullscreen-nav:hover {
      background: white;
      color: #255853;
      transform: scale(1.1);
    }

    .description-text {
      font-size: ${isMobile ? '1rem' : '1.1rem'};
      line-height: 1.8;
      color: #333;
      margin-bottom: 0;
      white-space: pre-line;
    }

    .tags-container {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    .tag-badge {
      padding: 6px 12px;
      font-size: 0.85rem;
      background: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 20px;
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }

    .related-section {
      margin-top: ${isMobile ? '20px' : '40px'};
    }

    .related-title {
      font-size: ${isMobile ? '1.3rem' : '1.6rem'};
      font-weight: 600;
      color: #255853;
      margin-bottom: 20px;
      border-bottom: 2px solid #e0e6ed;
      padding-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .related-grid {
      display: grid;
      grid-template-columns: ${isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))'};
      gap: ${isMobile ? '15px' : '20px'};
      margin-top: 20px;
    }

    .related-card {
      background: #fff;
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
      transition: transform 0.3s ease;
      text-decoration: none;
      color: inherit;
      display: block;
      height: 100%;
      border: 1px solid #e0e6ed;
    }

    .related-card:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.12);
      text-decoration: none;
      color: inherit;
    }

    .related-image-container {
      width: 100%;
      height: ${isMobile ? '140px' : '160px'};
      overflow: hidden;
      position: relative;
    }

    .related-content {
      padding: ${isMobile ? '12px' : '15px'};
    }

    .related-card-title {
      font-size: ${isMobile ? '0.95rem' : '1rem'};
      font-weight: 600;
      margin-bottom: 10px;
      color: #333;
      line-height: 1.4;
      height: 2.8em;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: white;
    }

    .empty-state {
      text-align: center;
      padding: 40px 20px;
    }

    @media (max-width: 576px) {
      .container {
        padding-left: 10px;
        padding-right: 10px;
      }
      
      .action-button {
        font-size: 0.85rem;
      }
      
      .meta-badge {
        font-size: 0.8rem;
        padding: 5px 10px;
      }
    }

    @media (max-width: 768px) {
      .slider-container {
        height: 250px;
      }
      
      .slider-controls {
        width: 30px;
        height: 30px;
      }
      
      .fullscreen-control {
        width: 35px;
        height: 35px;
        bottom: 10px;
        right: 10px;
      }
      
      .fullscreen-nav {
        width: 40px;
        height: 40px;
      }
    }
  `;

  if (loading) {
    return (
      <div className="content-detail-container">
        <style>{styles}</style>
        <div className="loading-container">
          <Spinner animation="border" variant="primary" />
          <span className="mt-3" style={{ fontSize: '1.2rem', color: '#255853' }}>
            Loading...
          </span>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="content-detail-container">
        <style>{styles}</style>
        <Container style={{ paddingTop: '80px' }}>
          <Alert variant="light" className="text-center" style={{ 
            backgroundColor: '#f8d7da',
            borderColor: '#f5c6cb',
            color: '#721c24',
            borderRadius: '10px',
            padding: '30px'
          }}>
            <Alert.Heading style={{ color: '#721c24' }}>
              {error || 'Content not found'}
            </Alert.Heading>
            <Button 
              variant="outline-primary" 
              onClick={() => navigate('/')} 
              className="mt-3"
              style={{ borderRadius: '25px', padding: '10px 25px' }}
            >
              Back to Home
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="content-detail-container">
      <Helmet>
        <title>{content.title} - KSA Files</title>
        <meta name="description" content={content.description} />
        <meta property="og:title" content={content.title} />
        <meta property="og:description" content={content.description} />
        <meta property="og:image" content={content.featured_media} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>

      <style>{styles}</style>

      {/* Toast Notifications */}
      <ToastContainer position="top-end" style={{ 
        zIndex: 9999, 
        marginTop: isMobile ? '60px' : '70px',
        right: isMobile ? '10px' : '20px'
      }}>
        <Toast 
          show={toast.show} 
          onClose={() => setToast({ ...toast, show: false })}
          delay={3000} 
          autohide
          bg={toast.variant}
        >
          <Toast.Body style={{ 
            color: toast.variant === 'danger' ? 'white' : 'inherit',
            fontFamily: "'ITF Qomra Arabic', sans-serif",
            textAlign: 'left'
          }}>
            {toast.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Back Button */}
      <div className="back-button">
        <button
          onClick={() => navigate(-1)}
          className="btn btn-outline-secondary btn-sm"
          style={{
            borderRadius: '20px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            padding: '8px 16px',
            backgroundColor: 'white'
          }}
        >
          <FaArrowLeft size={14} /> Back
        </button>
      </div>

      <Container fluid={isMobile} className={isMobile ? 'px-2' : ''}>
        {/* Header Section */}
        <div className="content-header">
          <div className="d-flex justify-content-between align-items-start flex-column">
            <div className="w-100">
              <h1 className="content-title">
                {content.title || content.title_ar}
              </h1>
              
              <div className="meta-info">
                <span className="meta-badge">
                  {getContentTypeIcon(content.content_type)}
                  {getContentTypeText(content.content_type)}
                </span>
                
                <span className="meta-badge" style={{ backgroundColor: '#357E77' }}>
                  <FaTag size={14} />
                  {content.category}
                </span>
                
                {content.is_breaking && (
                  <span className="meta-badge" style={{ backgroundColor: '#dc2626' }}>
                    ⚡ URGENT
                  </span>
                )}
                
                <span className="meta-text">
                  <FaUser size={14} /> {content.author || content.author_ar}
                </span>
                
                <span className="meta-text">
                  <FaCalendarAlt size={14} /> {formatDate(content.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* Share Button */}
          <div style={{ marginTop: '20px' }}>
            <button
              onClick={shareContent}
              className="action-button share-button"
              title="Share"
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              <FaShareAlt size={16} />
              <span>Share</span>
            </button>
          </div>
        </div>

        {/* Media Slider Section */}
        {sliderItems.length > 0 && (
          <div className="slider-section">
            <h3 className="slider-title">
              <FaImages size={16} />
              Media Gallery ({currentSlide + 1} of {sliderItems.length})
            </h3>
            
            <div className="slider-container" ref={sliderRef}>
              <div className="slider-wrapper">
                {sliderItems.map((item, index) => (
                  <div key={index} className={`slider-item ${index === currentSlide ? 'active' : ''}`}>
                    {renderSliderItem(item, index)}
                  </div>
                ))}
              </div>

              {/* Navigation Controls */}
              {sliderItems.length > 1 && (
                <>
                  <button className="slider-controls prev" onClick={goToPrevious}>
                    <FaChevronLeft />
                  </button>
                  <button className="slider-controls next" onClick={goToNext}>
                    <FaChevronRight />
                  </button>
                </>
              )}

              {/* Fullscreen Control - Always visible */}
              <button 
                className="fullscreen-control" 
                onClick={toggleFullscreen}
                title={showFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {showFullscreen ? <FaCompress /> : <FaExpand />}
              </button>
            </div>

            {/* Dots Navigation */}
            {sliderItems.length > 1 && (
              <div className="slider-dots">
                {sliderItems.map((_, index) => (
                  <button
                    key={index}
                    className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Description */}
        <Card className="mb-4" style={{ border: '1px solid #e0e6ed' }}>
          <Card.Body style={{ padding: isMobile ? '15px' : '25px' }}>
            <h5 className="text-primary mb-3 d-flex align-items-center" style={{ 
              color: '#255853'
            }}>
              <FaFileAlt className="me-2" size={16} />
              Description
            </h5>
            <div className="description-text">
              {content.description || content.description_ar}
            </div>
          </Card.Body>
        </Card>

        {/* Tags */}
        {content.tags && content.tags.length > 0 && (
          <Card className="mb-4" style={{ border: '1px solid #e0e6ed' }}>
            <Card.Body style={{ padding: isMobile ? '15px' : '25px' }}>
              <h5 className="text-primary mb-3 d-flex align-items-center" style={{ 
                color: '#255853'
              }}>
                <FaTag className="me-2" size={16} />
                Tags
              </h5>
              <div className="tags-container">
                {content.tags.map((tag, index) => (
                  <Badge key={index} className="tag-badge">
                    #{content.tags?.[index] || tag}
                  </Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Related Content */}
        {relatedContent.length > 0 && (
          <div className="related-section">
            <h3 className="related-title">
              <FaShareSquare size={20} />
              Related Content
            </h3>
            <div className="related-grid">
              {relatedContent.map((item) => (
                <Link 
                  key={item.id} 
                  to={`/content/${item.content_token || item.id}`}
                  className="related-card"
                >
                  <div className="related-image-container">
                    {item.content_type === 'video' ? (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaVideo size={40} color="#255853" />
                      </div>
                    ) : item.content_type === 'image' && item.featured_media ? (
                      <img
                        src={item.featured_media}
                        alt={item.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        background: '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FaFileAlt size={40} color="#255853" />
                      </div>
                    )}
                    
                    <Badge style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      fontSize: '0.75rem'
                    }}>
                      {getContentTypeIcon(item.content_type)}
                      <span>{getContentTypeText(item.content_type)}</span>
                    </Badge>
                    
                    {item.is_breaking && (
                      <Badge style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(220, 38, 38, 0.9)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        fontSize: '0.75rem'
                      }}>
                        <FaExclamationCircle size={10} />
                        <span>URGENT</span>
                      </Badge>
                    )}
                  </div>
                  
                  <div className="related-content">
                    <h5 className="related-card-title">
                      {item.title || item.title_ar}
                    </h5>
                    <div className="d-flex justify-content-between align-items-center text-muted small mt-3">
                      <span className="d-flex align-items-center">
                        {getContentTypeIcon(item.content_type)} 
                        <span className="ms-1">{getContentTypeText(item.content_type)}</span>
                      </span>
                      <span className="d-flex align-items-center">
                        <FaCalendarAlt className="me-1" size={12} />
                        {formatDate(item.created_at)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Fullscreen Modal */}
        {showFullscreen && (
          <div className="fullscreen-modal">
            <div className="fullscreen-header">
              <h3 className="fullscreen-title">
                {sliderItems[currentSlide]?.title || 'Media'} 
                ({currentSlide + 1} of {sliderItems.length})
              </h3>
              <button 
                className="fullscreen-close" 
                onClick={toggleFullscreen}
                title="Close fullscreen"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="fullscreen-content">
              {renderSliderItem(sliderItems[currentSlide], currentSlide, true)}
            </div>

            {sliderItems.length > 1 && (
              <div className="fullscreen-controls">
                <button 
                  className="fullscreen-nav" 
                  onClick={goToPrevious}
                  title="Previous"
                >
                  <FaChevronLeft size={24} />
                </button>
                <button 
                  className="fullscreen-nav" 
                  onClick={goToNext}
                  title="Next"
                >
                  <FaChevronRight size={24} />
                </button>
              </div>
            )}
          </div>
        )}
      </Container>
    </div>
  );
};

export default ContentDetail;